
import React, { useMemo } from 'react';
import { ScoreCard } from './ScoreCard';

export interface CategoryScoreGridProps {
  categoryScores: Record<string, number | { score: number; description: string }>;
}

// Remaining dynamic categories (AI Readiness will be rendered separately)
const REMAINING_CATEGORY_KEYS = [
  { key: 'content_quality', label: 'Content Quality', description: 'Evaluation of content depth, uniqueness, and overall quality' },
  { key: 'EEAT', label: 'E-E-A-T', description: 'Assessment of Experience, Expertise, Authoritativeness, and Trustworthiness' },
  { key: 'helpfulness', label: 'Helpfulness', description: 'How well the content serves user intent and provides practical value' },
  { key: 'shareability', label: 'Shareability', description: 'Social media potential and differentiation from similar content' }
];

export const CategoryScoreGrid: React.FC<CategoryScoreGridProps> = ({ categoryScores }) => {
  console.log('[RENDER] CategoryScoreGrid received:', categoryScores);
  
  // Helper functions for score processing
  const getScoreValue = (value: number | { score: number; description: string }): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value?.score !== undefined) return value.score;
    return 0;
  };

  const getLabel = (key: string): string => {
    const labels: Record<string, string> = {
      content_quality: 'Content Quality',
      EEAT: 'E-E-A-T',
      helpfulness: 'Helpfulness',
      shareability: 'Shareability',
      ai_optimization_score: 'AI Readiness'
    };
    return labels[key] || '';
  };

  const getDescription = (key: string): string => {
    const descriptions: Record<string, string> = {
      content_quality: 'Evaluation of content depth, uniqueness, and overall quality',
      EEAT: 'Assessment of Experience, Expertise, Authoritativeness, and Trustworthiness',
      helpfulness: 'How well the content serves user intent and provides practical value',
      shareability: 'Social media potential and differentiation from similar content',
      ai_optimization_score: 'How well content is optimized for AI processing and indexing'
    };
    return descriptions[key] || 'Score analysis';
  };
  
  // CONSOLIDATED PROCESSING - Single atomic operation to prevent timing issues
  const processedScores = useMemo(() => {
    const safeScores = structuredClone(categoryScores || {});
    
    // Create stable score entries with consistent keys
    const scoreEntries: Array<{
      id: string; // Stable identifier for React keys
      key: string; // Original data key
      label: string;
      description: string;
      value: number;
    }> = [];
    
    // Process each score with stable mapping
    Object.entries(safeScores).forEach(([key, rawValue]) => {
      // Handle AI optimization score specially to ensure it appears in top cards
      if (key === 'ai_optimization_score' || key === 'ai_readiness_proxy') {
        const score = getScoreValue(rawValue);
        if (Number.isFinite(score)) {
          scoreEntries.push({
            id: 'ai-readiness', // Stable ID to prevent duplicate keys
            key: key,
            label: 'AI Readiness',
            description: 'How well content is optimized for AI processing and indexing',
            value: score
          });
        }
        return;
      }
      
      // Process other scores normally
      const score = getScoreValue(rawValue);
      if (Number.isFinite(score)) {
        const label = getLabel(key);
        const description = getDescription(key);
        
        if (label) {
          scoreEntries.push({
            id: key, // Use original key as stable ID for other scores
            key,
            label,
            description,
            value: score
          });
        }
      }
    });
    
    console.log('🔧 Processed score entries:', scoreEntries);
    return scoreEntries;
  }, [categoryScores]);
  
  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
        {/* STABLE RENDERING - Use processed scores with stable keys */}
        {processedScores.map((scoreEntry) => {
          console.log(`✅ Rendering ${scoreEntry.label}: score=${scoreEntry.value}`);

          return (
            <ScoreCard
              key={scoreEntry.id} // Stable React key
              title={scoreEntry.label}
              score={scoreEntry.value}
              maxScore={20}
              description={scoreEntry.description}
            />
          );
        })}
      </div>
    </div>
  );
};
